/**
 * Eligibility Calculator - Shared eligibility logic for TRONC distribution
 * This file mirrors the backend EligibilityCalculator.cs logic.
 *
 * Architecture: Both BE (C#) and FE (JS) must produce identical results
 * for the same inputs. All rule checks are applied in the same order.
 */

// ============================================================================
// REASON CODES (must match backend EligibilityReasonCode enum)
// ============================================================================

export const EligibilityReasonCode = {
  EmployeeNotActive: 'EmployeeNotActive',
  EmployeeNotEligible: 'EmployeeNotEligible',
  NoJobRoleAssigned: 'NoJobRoleAssigned',
  JobRoleNotActive: 'JobRoleNotActive',
  JobRoleNotEligible: 'JobRoleNotEligible',
  InProbation: 'InProbation',
  ManagementRoleExcluded: 'ManagementRoleExcluded',
  InsufficientServiceDays: 'InsufficientServiceDays',
  InsufficientHoursWorked: 'InsufficientHoursWorked',
  EmploymentNotStarted: 'EmploymentNotStarted',
  EmploymentEnded: 'EmploymentEnded',
};

// Human-readable labels for reason codes
export const EligibilityReasonLabels = {
[EligibilityReasonCode.EmployeeNotActive]: 'Employee is not active',
  [EligibilityReasonCode.EmployeeNotEligible]: 'Employee is marked as not eligible for TRONC',
  [EligibilityReasonCode.NoJobRoleAssigned]: 'Employee has no job role assigned',
  [EligibilityReasonCode.JobRoleNotActive]: "Employee's job role is not active",
  [EligibilityReasonCode.JobRoleNotEligible]: "Employee's job role is not eligible for TRONC",
  [EligibilityReasonCode.InProbation]: 'Employee is in probation period',
  [EligibilityReasonCode.ManagementRoleExcluded]: 'Management roles are excluded from TRONC',
  [EligibilityReasonCode.InsufficientServiceDays]: 'Employee has not met minimum service days requirement',
  [EligibilityReasonCode.InsufficientHoursWorked]: 'Employee has not worked minimum required hours in period',
  [EligibilityReasonCode.EmploymentNotStarted]: 'Employee has not started employment',
  [EligibilityReasonCode.EmploymentEnded]: "Employee's employment has ended",
};

// ============================================================================
// DEFAULT ELIGIBILITY RULES
// ============================================================================

/**
 * Default eligibility rules with standard settings.
 * Matches backend EligibilityRules.Default
 */
export function getDefaultEligibilityRules() {
  return {
    // Basic requirements
    requireEmployeeIsActive: true,
    requireEmployeeIsEligible: true,
    requireRoleIsEligible: true,
    requireRoleIsActive: true,

    // Probation rules
  excludeEmployeesInProbation: false,

    // Management rules
    excludeManagementRoles: false,

    // Minimum service requirements
requireMinimumServiceDays: false,
    minimumServiceDays: 0,

    // Hours requirements
    requireMinimumHoursInPeriod: false,
    minimumHoursInPeriod: 0,

 // Employment date rules
    requireEmploymentStarted: true,
    excludeTerminatedEmployees: true,
  };
}

/**
 * Permissive eligibility rules � only checks basic flags.
 * Matches backend EligibilityRules.Permissive
 */
export function getPermissiveEligibilityRules() {
  return {
    ...getDefaultEligibilityRules(),
  };
}

/**
 * Strict eligibility rules � includes all checks.
 * Matches backend EligibilityRules.Strict
 */
export function getStrictEligibilityRules() {
  return {
    ...getDefaultEligibilityRules(),
    excludeEmployeesInProbation: true,
    requireMinimumServiceDays: true,
    minimumServiceDays: 30,
    requireMinimumHoursInPeriod: true,
    minimumHoursInPeriod: 1,
  };
}

// ============================================================================
// ELIGIBILITY CALCULATOR
// ============================================================================

/**
 * Calculates eligibility for a single employee.
 *
 * @param {Object} employee - Employee data
 * @param {string} employee.id - Employee ID
 * @param {string} employee.name - Employee full name
 * @param {boolean} employee.isActive - Whether the employee is active
 * @param {boolean} employee.isEligibleForTronc - Whether the employee is flagged as eligible
 * @param {string|null} employee.jobRoleId - Primary job role ID
 * @param {string|null} employee.startDate - Employment start date (ISO string)
 * @param {string|null} employee.endDate - Employment end date (ISO string)
 * @param {number|null} employee.probationPeriodDays - Probation period in days
 * @param {boolean} employee.hasPassedProbation - Whether probation has been passed
 *
 * @param {Object|null} jobRole - Job role data (null if not assigned)
 * @param {string} jobRole.id - Job role ID
 * @param {string} jobRole.name - Job role name
 * @param {boolean} jobRole.isActive - Whether the role is active
 * @param {boolean} jobRole.isEligibleForTronc - Whether the role is eligible for TRONC
 * @param {boolean} jobRole.isManagement - Whether the role is a management role
 *
 * @param {Object} context - Calculation context
 * @param {string} context.date - Distribution date (ISO string or YYYY-MM-DD)
 * @param {Object} context.rules - Eligibility rules (from getDefaultEligibilityRules)
 * @param {Array} context.clockRecords - Clock records [{employeeId, date, hoursWorked}]
 *
 * @returns {Object} Eligibility result {employeeId, employeeName, isEligible, reasons[]}
 */
export function calculateForEmployee(employee, jobRole, context) {
  const reasons = [];
  const rules = context.rules || getDefaultEligibilityRules();
  const date = parseDate(context.date);

  // ---- Basic employee checks ----
  if (rules.requireEmployeeIsActive && !employee.isActive) {
    reasons.push({
      code: EligibilityReasonCode.EmployeeNotActive,
      description: EligibilityReasonLabels[EligibilityReasonCode.EmployeeNotActive],
    });
  }

  if (rules.requireEmployeeIsEligible && !employee.isEligibleForTronc) {
    reasons.push({
      code: EligibilityReasonCode.EmployeeNotEligible,
      description: EligibilityReasonLabels[EligibilityReasonCode.EmployeeNotEligible],
    });
  }

  // ---- Job role checks ----
  if (jobRole == null && (rules.requireRoleIsEligible || rules.requireRoleIsActive)) {
    reasons.push({
      code: EligibilityReasonCode.NoJobRoleAssigned,
      description: EligibilityReasonLabels[EligibilityReasonCode.NoJobRoleAssigned],
  });
  }

  if (jobRole != null) {
 if (rules.requireRoleIsActive && !jobRole.isActive) {
      reasons.push({
        code: EligibilityReasonCode.JobRoleNotActive,
        description: EligibilityReasonLabels[EligibilityReasonCode.JobRoleNotActive],
});
    }

    if (rules.requireRoleIsEligible && !jobRole.isEligibleForTronc) {
    reasons.push({
        code: EligibilityReasonCode.JobRoleNotEligible,
        description: EligibilityReasonLabels[EligibilityReasonCode.JobRoleNotEligible],
  });
    }

    if (rules.excludeManagementRoles && jobRole.isManagement) {
      reasons.push({
        code: EligibilityReasonCode.ManagementRoleExcluded,
        description: EligibilityReasonLabels[EligibilityReasonCode.ManagementRoleExcluded],
      });
    }
  }

  // ---- Probation check ----
  if (rules.excludeEmployeesInProbation) {
    const probation = isEmployeeInProbation(employee, date);
    if (probation.inProbation) {
    reasons.push({
      code: EligibilityReasonCode.InProbation,
        description: EligibilityReasonLabels[EligibilityReasonCode.InProbation],
     details: `${probation.daysRemaining} days remaining`,
    });
    }
  }

  // ---- Minimum service days check ----
  if (rules.requireMinimumServiceDays && rules.minimumServiceDays > 0) {
    const serviceDays = calculateServiceDays(employee, date);
    if (serviceDays < rules.minimumServiceDays) {
      reasons.push({
  code: EligibilityReasonCode.InsufficientServiceDays,
        description: EligibilityReasonLabels[EligibilityReasonCode.InsufficientServiceDays],
  details: `${serviceDays} days of service, requires ${rules.minimumServiceDays} days`,
      });
    }
  }

  // ---- Employment start date check ----
  if (rules.requireEmploymentStarted && employee.startDate) {
    const startDate = parseDate(employee.startDate);
    if (startDate > date) {
      reasons.push({
  code: EligibilityReasonCode.EmploymentNotStarted,
      description: EligibilityReasonLabels[EligibilityReasonCode.EmploymentNotStarted],
        details: `Starts on ${formatDate(startDate)}, distribution date is ${formatDate(date)}`,
      });
    }
  }

  // ---- Employment end date check ----
  if (rules.excludeTerminatedEmployees && employee.endDate) {
    const endDate = parseDate(employee.endDate);
    if (endDate < date) {
      reasons.push({
        code: EligibilityReasonCode.EmploymentEnded,
        description: EligibilityReasonLabels[EligibilityReasonCode.EmploymentEnded],
        details: `Ended on ${formatDate(endDate)}, distribution date is ${formatDate(date)}`,
      });
    }
  }

  // ---- Minimum hours check ----
  if (rules.requireMinimumHoursInPeriod && rules.minimumHoursInPeriod > 0) {
    const hoursWorked = calculateHoursWorked(employee.id, context.clockRecords || []);
    if (hoursWorked < rules.minimumHoursInPeriod) {
      reasons.push({
        code: EligibilityReasonCode.InsufficientHoursWorked,
        description: EligibilityReasonLabels[EligibilityReasonCode.InsufficientHoursWorked],
        details: `${hoursWorked.toFixed(1)} hours worked, requires ${rules.minimumHoursInPeriod.toFixed(1)} hours`,
      });
    }
  }

  return {
    employeeId: employee.id,
    employeeName: employee.name || employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
    isEligible: reasons.length === 0,
    reasons,
  };
}

/**
 * Calculates eligibility for multiple employees.
 *
 * @param {Array} employees - Array of employee objects
 * @param {Object} jobRolesMap - Map of job role ID -> job role object
 * @param {Object} context - Calculation context {date, rules, clockRecords}
 * @returns {Array} Array of eligibility results
 */
export function calculateForEmployees(employees, jobRolesMap, context) {
  return (employees || []).map(employee => {
 const jobRoleId = employee.jobRoleId || employee.primaryJobRoleId;
    const jobRole = jobRoleId ? (jobRolesMap[jobRoleId] || null) : null;
  return calculateForEmployee(employee, jobRole, context);
  });
}

/**
 * Filters employees to only those who are eligible.
 *
 * @param {Array} employees - Array of employee objects
 * @param {Object} jobRolesMap - Map of job role ID -> job role object
 * @param {Object} context - Calculation context {date, rules, clockRecords}
 * @returns {Array} Array of eligible employees only
 */
export function filterEligibleEmployees(employees, jobRolesMap, context) {
  const results = calculateForEmployees(employees, jobRolesMap, context);
  const eligibleIds = new Set(
    results.filter(r => r.isEligible).map(r => r.employeeId)
  );
  return (employees || []).filter(e => eligibleIds.has(e.id));
}

/**
 * Gets a summary of eligibility across all employees.
 *
 * @param {Array} results - Array of eligibility results from calculateForEmployees
 * @returns {Object} Summary {totalEmployees, eligibleCount, ineligibleCount, eligiblePercentage, ineligiblePercentage, reasonCounts}
 */
export function getEligibilitySummary(results) {
  const total = results.length;
  const eligible = results.filter(r => r.isEligible).length;
  const ineligible = total - eligible;

  // Group ineligibility reasons
  const reasonCounts = {};
  results
    .filter(r => !r.isEligible)
    .forEach(r => {
      (r.reasons || []).forEach(reason => {
        reasonCounts[reason.code] = (reasonCounts[reason.code] || 0) + 1;
      });
    });

  return {
    totalEmployees: total,
    eligibleCount: eligible,
    ineligibleCount: ineligible,
    eligiblePercentage: total > 0 ? (eligible / total) * 100 : 0,
    ineligiblePercentage: total > 0 ? (ineligible / total) * 100 : 0,
    reasonCounts,
  };
}

// ============================================================================
// CONVERSION HELPERS
// ============================================================================

/**
 * Converts an API employee object to the shape expected by the eligibility calculator.
 *
 * @param {Object} apiEmployee - Employee from API response
 * @returns {Object} Employee in eligibility calculator format
 */
export function toEligibilityEmployee(apiEmployee) {
  return {
    id: apiEmployee.id,
    name: apiEmployee.fullName || `${apiEmployee.firstName || ''} ${apiEmployee.lastName || ''}`.trim(),
    isActive: apiEmployee.isActive ?? true,
    isEligibleForTronc: apiEmployee.isEligibleForTronc ?? true,
jobRoleId: apiEmployee.primaryJobRoleId || apiEmployee.jobRoleId || null,
    startDate: apiEmployee.startDate || null,
    endDate: apiEmployee.endDate || null,
    probationPeriodDays: apiEmployee.probationPeriodDays || null,
    hasPassedProbation: apiEmployee.hasPassedProbation ?? true,
  };
}

/**
 * Converts an API job role object to the shape expected by the eligibility calculator.
 *
 * @param {Object} apiJobRole - Job role from API response
 * @returns {Object} Job role in eligibility calculator format
 */
export function toEligibilityJobRole(apiJobRole) {
  return {
    id: apiJobRole.id,
    name: apiJobRole.name,
    isActive: apiJobRole.isActive ?? true,
    isEligibleForTronc: apiJobRole.isEligibleForTronc ?? true,
    isManagement: apiJobRole.isManagement ?? false,
  };
}

/**
 * Builds a job roles map from an array of API job roles.
 *
 * @param {Array} jobRoles - Array of job role objects
 * @returns {Object} Map of job role ID -> eligibility job role
 */
export function buildJobRolesMap(jobRoles) {
  const map = {};
  (jobRoles || []).forEach(role => {
    const eligRole = toEligibilityJobRole(role);
    map[eligRole.id] = eligRole;
  });
  return map;
}

/**
 * Converts an API clock record to the shape expected by the eligibility calculator.
 *
 * @param {Object} apiClockRecord - Clock record from API response
 * @returns {Object} Clock record in eligibility calculator format
 */
export function toEligibilityClockRecord(apiClockRecord) {
  let hoursWorked = apiClockRecord.hoursWorked || 0;

  // Fallback: calculate from clockIn/clockOut if hoursWorked is not set
  if (hoursWorked === 0 && apiClockRecord.clockIn && apiClockRecord.clockOut) {
    const clockIn = new Date(apiClockRecord.clockIn);
    const clockOut = new Date(apiClockRecord.clockOut);
    const breakMinutes = apiClockRecord.actualBreakMinutes || 0;
    hoursWorked = (clockOut - clockIn) / (1000 * 60 * 60) - breakMinutes / 60;
    if (hoursWorked < 0) hoursWorked = 0;
  }

  return {
    employeeId: apiClockRecord.employeeId,
    date: apiClockRecord.clockIn
      ? new Date(apiClockRecord.clockIn).toISOString().split('T')[0]
      : apiClockRecord.date,
    hoursWorked,
  };
}

// ============================================================================
// PRIVATE HELPERS
// ============================================================================

/**
 * Parses a date value to a Date object (midnight UTC).
 * Accepts ISO strings, YYYY-MM-DD, or Date objects.
 */
function parseDate(value) {
  if (!value) return new Date();
  if (value instanceof Date) return stripTime(value);
  const d = new Date(value);
  return stripTime(d);
}

/**
 * Strips time from a Date, keeping only the date portion (midnight).
 */
function stripTime(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Formats a Date as YYYY-MM-DD.
 */
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Checks whether an employee is currently in their probation period.
 */
function isEmployeeInProbation(employee, date) {
  // If HasPassedProbation is already set, use it
  if (employee.hasPassedProbation) {
    return { inProbation: false, daysRemaining: 0 };
  }

  // If no start date, assume passed
  if (!employee.startDate) {
 return { inProbation: false, daysRemaining: 0 };
  }

  // If no probation period defined, assume passed
  if (!employee.probationPeriodDays || employee.probationPeriodDays <= 0) {
    return { inProbation: false, daysRemaining: 0 };
  }

  const startDate = parseDate(employee.startDate);
  const probationEnd = new Date(startDate);
  probationEnd.setDate(probationEnd.getDate() + employee.probationPeriodDays);

  if (date >= probationEnd) {
    return { inProbation: false, daysRemaining: 0 };
  }

  const daysRemaining = Math.ceil((probationEnd - date) / (1000 * 60 * 60 * 24));
  return { inProbation: true, daysRemaining };
}

/**
 * Calculates the number of service days from start date to distribution date.
 */
function calculateServiceDays(employee, date) {
  if (!employee.startDate) return 0;

  const startDate = parseDate(employee.startDate);
  if (startDate > date) return 0;

  return Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
}

/**
 * Calculates total hours worked from clock records for a given employee.
 */
function calculateHoursWorked(employeeId, clockRecords) {
  return (clockRecords || [])
    .filter(c => c.employeeId === employeeId)
    .reduce((sum, c) => sum + (c.hoursWorked || 0), 0);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  EligibilityReasonCode,
  EligibilityReasonLabels,
  getDefaultEligibilityRules,
  getPermissiveEligibilityRules,
  getStrictEligibilityRules,
  calculateForEmployee,
  calculateForEmployees,
  filterEligibleEmployees,
  getEligibilitySummary,
  toEligibilityEmployee,
  toEligibilityJobRole,
  toEligibilityClockRecord,
  buildJobRolesMap,
};
